import { STRAPI_URL, parseErrorMessage, strapiAuthHeaders } from './strapi';

export type NavigationMenuItem = {
  access_code: string;
  access_name: string;
  href: string | null;
};

export type NavigationMenuGroup = {
  key: string;
  label: string;
  items: NavigationMenuItem[];
};

export type NavigationMenuPayload = {
  groups: NavigationMenuGroup[];
  /** Every `access_code` on the user’s preset (including codes without a nav link). */
  access_codes?: string[];
};

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

/** Signed-in user's left nav, derived from their permission preset (`nav_path` on each access). */
export async function strapiFetchNavigationMenu(jwt: string): Promise<NavigationMenuPayload> {
  // Navigation menu endpoint must remain `/api/navigation-menu` (gateway/proxy maps it). Do not replace with `/api/permission-presets/navigation-menu`.
  const res = await fetch(`${STRAPI_URL}/api/navigation-menu`, {
    headers: strapiAuthHeaders(jwt),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) {
    throw new Error(parseErrorMessage(body, res.status));
  }
  const b = asRecord(body);
  const data = b?.data && typeof b.data === 'object' ? (b.data as Record<string, unknown>) : null;
  const groupsRaw = data?.groups;
  if (!Array.isArray(groupsRaw)) {
    const codesRawEarly = data?.access_codes;
    const access_codes_early = Array.isArray(codesRawEarly)
      ? (codesRawEarly as unknown[]).map((c) => String(c))
      : undefined;
    return { groups: [], access_codes: access_codes_early };
  }
  const groups: NavigationMenuGroup[] = groupsRaw.map((g) => {
    const gr = g as Record<string, unknown>;
    const itemsRaw = gr.items;
    const items: NavigationMenuItem[] = Array.isArray(itemsRaw)
      ? (itemsRaw as Record<string, unknown>[]).map((it) => ({
          access_code: String(it.access_code ?? ''),
          access_name: String(it.access_name ?? ''),
          href: it.href != null && String(it.href).trim() !== '' ? String(it.href) : null,
        }))
      : [];
    return {
      key: String(gr.key ?? ''),
      label: String(gr.label ?? ''),
      items,
    };
  });
  const codesRaw = data?.access_codes;
  const access_codes = Array.isArray(codesRaw)
    ? (codesRaw as unknown[]).map((c) => String(c))
    : undefined;
  return { groups, access_codes };
}
